
import { defineComponent } from "vue";
import { filterReset, titleCase } from "@/util/helpers";
import { get } from "lodash-es";
import { BottomTab, GDT, IDropdownItem } from "@/types";
import { BFormInput } from "bootstrap-vue";
import GDropdown from "@/components/shared/GDropdown.vue";
import DatasetSelector from "@/components/shared/DatasetSelector.vue";
import DataSourceFilter from "@/components/drawers/right/DataSourceFilter.vue";
import BoundaryFilter from "@/components/drawers/right/BoundaryFilter.vue";
import { useDataSource, useGammaData, useLayout } from "@/services";

export default defineComponent({
  name: "DataSourceFilters",
  components: {
    BoundaryFilter,
    DatasetSelector,
    DataSourceFilter,
    GDropdown,
  },
  props: {
    sourceName: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    selectedGeometry: 0,
    inOperatorValues: [] as any[],
  }),
  computed: {
    polygonFilter(): GDT.DataFilter | null {
      return useDataSource(this.sourceName)().polygonFilter;
    },
    useScreenFilter: {
      get(): boolean {
        return useDataSource(this.sourceName)().useScreenFilter;
      },
      set(val: boolean) {
        useDataSource(this.sourceName)().setSpatialFilter(val, "screen");
        useGammaData().fetchAccumulations();
      },
    },
    usePolygonFilter: {
      get(): boolean {
        return useDataSource(this.sourceName)().usePolygonFilter;
      },
      set(val: boolean) {
        useDataSource(this.sourceName)().setSpatialFilter(val, "polygon");
      },
    },
    activeColumnType(): string {
      return useGammaData().activeColumnType;
    },
    activeFilterOperators(): IDropdownItem<GDT.FilterOperatorOption>[] {
      return useGammaData().applicableFilters.map((f) => ({
        key: f.id,
        text: f.name,
        value: f,
      }));
    },
    tableDefinition(): GDT.TableDefinition | null {
      return useDataSource(this.sourceName)().tableDefinition;
    },
    filters(): GDT.DataFilter[] {
      return [...useDataSource(this.sourceName)().userFilters];
    },
    columnDefinitionItems(): IDropdownItem<GDT.ColumnDefinition>[] {
      return useDataSource(this.sourceName)()
        .bottomColumnDefinitions.filter((c) => c.filterable)
        .map((c) => ({
          key: c.columnName,
          text: titleCase(c.columnName),
          value: c,
        }));
    },
    selectedFilter: {
      get(): GDT.DataFilter {
        return (
          useGammaData().selectedFilter || useGammaData().applicableFilters[0]
        );
      },
      set(filter: GDT.DataFilter) {
        useGammaData().selectedFilter = filter;
      },
    },
    inputType(): string {
      return this.selectedFilter.fieldName
        ? GDT.castInputType(GDT.ColumnTypes.indexOf(this.activeColumnType))
        : "text";
    },
    geometryOptions(): IDropdownItem<number>[] {
      return [
        { text: "Screen bounds", value: 0, key: 0 },
        { text: "Draw polygon", value: 1, key: 1 },
      ];
    },
    geometryText(): string {
      return this.geometryOptions[this.selectedGeometry].text || "";
    },
    isInOperator: {
      get(): boolean {
        return this.selectedFilter.filterOperator
          ? this.filterOperatorSymbol(this.selectedFilter.filterOperator) ===
              "[]"
          : false;
      },
      set() {
        /* required for v-model */
      },
    },
    titleCase: () => titleCase,
  },
  methods: {
    openDrawer() {
      useLayout().setBottomActiveTab(BottomTab.Accumulations, true);
    },
    selectColumn(col: GDT.ColumnDefinition) {
      this.selectedFilter = {
        ...this.selectedFilter,
        fieldName: col.columnName,
      };
      useGammaData().activeColumnType = col.type;
      this.selectFilterOperator(this.activeFilterOperators[0].value);
    },
    addFilter() {
      if (!this.selectedFilter.fieldName) {
        this.openDropdown("columnNameDd");
      } else if (
        !(this.isInOperator || this.selectedFilter.value) ||
        (this.isInOperator && !this.inOperatorValues.length)
      ) {
        this.focusInput();
      } else {
        useGammaData().addDataFilter({
          sourceName: this.sourceName,
          filter: {
            fieldName: this.selectedFilter.fieldName,
            filterOperator: this.selectedFilter.filterOperator,
            value: this.isInOperator
              ? this.inOperatorValues
              : this.inputType === "number"
              ? Number(this.selectedFilter.value)
              : this.selectedFilter.value,
          } as GDT.DataFilter,
        });
        this.selectedFilter = filterReset;
        this.inOperatorValues = [];
      }
    },
    focusInput() {
      this.$nextTick().then(() => {
        const input = get(this.$refs, "filterValue.$el");
        requestAnimationFrame(() => {
          if (input) {
            (input as HTMLInputElement).focus();
          }
        });
      });
    },
    openDropdown(key: string) {
      const dd: any = this.$refs[key];
      requestAnimationFrame(() => {
        if (dd) {
          dd.open();
        }
      });
    },
    closeDropdown(key: string) {
      const dd: any = this.$refs[key];
      requestAnimationFrame(() => {
        if (dd) {
          dd.close();
          if (key === "filterOperatorDd" && this.isInOperator) {
            const input: any = this.$refs.filterValue;
            if (input instanceof BFormInput) {
              input.focus();
            }
          }
        }
      });
    },
    removeFilter(index: number) {
      useGammaData().removeDataFilter({
        sourceName: this.sourceName,
        index,
      });
    },
    selectFilterOperator(operator: GDT.FilterOperatorOption) {
      this.selectedFilter.filterOperator = operator.id;
      this.closeDropdown("filterOperatorDd");
    },
    filterOperatorSymbol(operator: GDT.FilterOperator) {
      return (
        this.activeFilterOperators.find((o) => o.value.id === operator)?.value
          ?.symbol || ""
      );
    },
    filterOperatorName(operator: GDT.FilterOperator) {
      return (
        this.activeFilterOperators.find((o) => o.value.id === operator)?.value
          ?.name || ""
      );
    },
    getDropdownHeight(n: number) {
      return { height: Math.min(n, 8) * 34 + "px", marginBottom: "-2px" };
    },
    onEnterPressed() {
      if (this.isInOperator && this.selectedFilter.value) {
        const value =
          this.inputType === "number"
            ? Number(this.selectedFilter.value)
            : this.selectedFilter.value;
        if (!this.inOperatorValues.includes(value)) {
          this.inOperatorValues.push(value);
        }
        this.selectedFilter.value = null;
      }
    },
    removeInValue(value: string | number) {
      if (this.inOperatorValues.includes(value)) {
        this.inOperatorValues = this.inOperatorValues.filter(
          (v) => v !== value
        );
      }
    },
  },
});
